import {
    RenderableDocument,
    DibJsonNode,
    DibObjectAccline,
    DibObjectTaxline,
    ElementDataAccounting,
} from "../../types";
import type {OpenPanelRequest} from "../../panel/PanelContext";
import {usePanel} from "../../panel/PanelContext";
import contentLinkClasses from "../../components/contentLink.module.css";
import classes from "./accountingTable.module.css";

const VAT = "vat" as const;
const PAYROLL = "payroll" as const;
const ACCOUNTS = "accounts" as const;

type AccountType = typeof VAT | typeof PAYROLL | typeof ACCOUNTS;

const accIdToType: Record<string, AccountType> = {
    "1": VAT,
    "2": PAYROLL,
    "3": ACCOUNTS,
};

const debetCreditLabels: Record<number, string> = {
    1: "Debet",
    2: "Kredit",
};

const headerLabels: Record<AccountType, string> = {
    [VAT]: "MVA",
    [ACCOUNTS]: "Konto",
    [PAYROLL]: "Lønn",
};

const headerClasses: Record<AccountType, string> = {
    [VAT]: classes.headerVat,
    [ACCOUNTS]: classes.headerAccounts,
    [PAYROLL]: classes.headerPayroll,
};

/** WARNING: Bad hack, ikke hardkode inn disse :( */
const accountingTaxes = [
    {id: "1", name: "Skatteplikt", accId: "2", dataId: "1238502816"},
    {id: "2", name: "Trekkplikt", accId: "2", dataId: "428720803"},
    {id: "3", name: "AGA-plikt", accId: "2", dataId: "1420146649"},
    {id: "4", name: "Opplysningsplikt", accId: "2", dataId: "-1033824201"},
    {id: "5", name: "Feriepenger", accId: "2", dataId: "-2050210070"},
    {
        id: "6",
        name: "Sykepenger",
        accId: "2",
        dataId: "72b31bcb-381c-4f6d-8de7-520d531a0d3a",
    },
];

const handlePanelClick = (
    e: React.MouseEvent,
    id: string,
    resourceId: string,
    openPanel: (request: OpenPanelRequest) => void
) => {
    e.preventDefault();
    openPanel({
        label: "Account",
        fetchType: "documentParts",
        params: {resourceId, segmentId: "preview", id},
    });
};

const AcclineRow = ({
    accline,
    openPanel,
}: {
    accline: DibObjectAccline;
    openPanel: (request: OpenPanelRequest) => void;
}) => (
    <td>
        <div className={classes.cellWrapper}>
            <a
                href="#"
                className={contentLinkClasses.panelLink}
                onClick={(e) =>
                    handlePanelClick(
                        e,
                        accline.data.dataId,
                        accline.data.dataResourceId,
                        openPanel
                    )
                }
            >
                {accline.data.name}
            </a>
            {!!accline.data.debetcredit && (
                <span className={classes.debetCredit}>
                    {debetCreditLabels[accline.data.debetcredit]}
                </span>
            )}
        </div>
    </td>
);

const AcclineContainer = ({
    acclines,
    accountType,
    openPanel,
}: {
    acclines: DibObjectAccline[];
    accountType: AccountType;
    openPanel: (request: OpenPanelRequest) => void;
}) => {
    if (acclines.length === 0) return null;

    return (
        <tbody>
            {acclines.map((a, index) => (
                <tr key={a.data.id}>
                    {index === 0 && (
                        <th
                            className={headerClasses[accountType]}
                            rowSpan={acclines.length}
                        >
                            {headerLabels[accountType]}
                        </th>
                    )}
                    <AcclineRow accline={a} openPanel={openPanel} />
                </tr>
            ))}
        </tbody>
    );
};

const TaxRow = ({
    taxlines,
    acclines,
    openPanel,
}: {
    taxlines: DibObjectTaxline[];
    acclines: DibObjectAccline[];
    openPanel: (request: OpenPanelRequest) => void;
}) => {
    const dataResourceId = taxlines.reduce(
        (acc, current) => acc || current.data.dataResourceId,
        ""
    );

    const truthyTaxlines = taxlines
        .filter((t) => t.data?.accId === "2")
        .map((t) => t.data.taxId);

    return (
        <tr>
            {acclines.length === 0 && (
                <th className={headerClasses[PAYROLL]} rowSpan={1}>
                    {headerLabels[PAYROLL]}
                </th>
            )}
            <td>
                <div className={classes.taxRow}>
                    {accountingTaxes.map((t) => {
                        const isTruthy = truthyTaxlines.includes(t.id);
                        return (
                            <a
                                key={t.dataId}
                                href="#"
                                className={`${classes.taxLink} ${isTruthy ? classes.taxTruthy : classes.taxFalsy}`}
                                onClick={(e) =>
                                    handlePanelClick(
                                        e,
                                        t.dataId,
                                        dataResourceId,
                                        openPanel
                                    )
                                }
                            >
                                {t.name}
                            </a>
                        );
                    })}
                </div>
            </td>
        </tr>
    );
};

const PayrollContainer = ({
    acclines,
    taxlines,
    openPanel,
}: {
    acclines: DibObjectAccline[];
    taxlines: DibObjectTaxline[];
    openPanel: (request: OpenPanelRequest) => void;
}) => {
    if (acclines.length === 0 && taxlines.length === 0) return null;

    const rowSpan =
        taxlines.length > 0 ? acclines.length + 1 : acclines.length;

    return (
        <tbody>
            {acclines.map((a, index) => (
                <tr key={a.data.id}>
                    {index === 0 && (
                        <th
                            className={headerClasses[PAYROLL]}
                            rowSpan={rowSpan}
                        >
                            {headerLabels[PAYROLL]}
                        </th>
                    )}
                    <AcclineRow accline={a} openPanel={openPanel} />
                </tr>
            ))}
            {taxlines.length > 0 && (
                <TaxRow
                    taxlines={taxlines}
                    acclines={acclines}
                    openPanel={openPanel}
                />
            )}
        </tbody>
    );
};

export const AccountingTable = ({
    node,
    topicData,
}: {
    augmentation: string;
    node: DibJsonNode;
    topicData: RenderableDocument;
}) => {
    const {openPanel} = usePanel();

    const elementData = node.elementData as ElementDataAccounting | null;
    const accounting = elementData?.accounting ?? [];
    const tax = elementData?.tax ?? [];

    const groupedAcclines = accounting.reduce<
        Record<AccountType, DibObjectAccline[]>
    >(
        (accumulator, current) => {
            const object = topicData.objects[current] as DibObjectAccline;
            const accountType = accIdToType[object.data.accId];
            if (!accountType) return accumulator;
            return {
                ...accumulator,
                [accountType]: [...accumulator[accountType], object],
            };
        },
        {
            [VAT]: [],
            [PAYROLL]: [],
            [ACCOUNTS]: [],
        }
    );

    const taxObjects = tax.map(
        (t) => topicData.objects[t] as DibObjectTaxline
    );

    return (
        <table className={classes.table}>
            <AcclineContainer
                acclines={groupedAcclines[VAT]}
                accountType={VAT}
                openPanel={openPanel}
            />
            <AcclineContainer
                acclines={groupedAcclines[ACCOUNTS]}
                accountType={ACCOUNTS}
                openPanel={openPanel}
            />
            <PayrollContainer
                acclines={groupedAcclines[PAYROLL]}
                taxlines={taxObjects}
                openPanel={openPanel}
            />
        </table>
    );
};
